import React, { useEffect, useRef, useState } from 'react'
import TableLoader from '../../../genericComponents/oldGenericComponents/tableHelperComponents/TableLoader';
import { useDispatch, useSelector } from 'react-redux';
import { CONFIG } from '../../../config';
import { dispatchHandler, handleApiChange } from '../../../helper/apiHelperFunctions';
import { getPermissionKey } from '../../../helper/permissions';
import { useVirtualizer } from '@tanstack/react-virtual';
import TextWithTooltip from '../../../genericComponents/TextWithTooltip';
import GenericInput from '../../../genericComponents/input/GenericInputComponent';
import { Search, ArrowUp, ArrowDown, ChevronDown } from 'lucide-react';
import GenericFormHeaderComponent from '../../../genericComponents/forms/GenericFormHeaderComponent';
import { modifyDataForDocumentViewerUrlInsertion } from '../../../helper/helperTableFunctions';
import { getPayloadFromURLParams, setPayloadAsURLParams } from '../../../helper/URLParamsHelper';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import TableMainLevelFilters from '../../../genericComponents/oldGenericComponents/TableMainLevelFilters';
import TNATimelinePopover from './TNATimelinePopover';

function ViewCountData(props) {

    const { selectedData } = props || {};
    const cellFilter = selectedData?.filter || {};
    const cellStatusName = selectedData?.statusName || "";

    const jumpInputRef = useRef();

    const getTableComponentListData = useSelector(state => state.generalStore.getTableComponentListData);
    const generalStore = useSelector(state => state.generalStore);
    const getDropdownData = useSelector(state => state?.generalStore?.getDropdownData);

    const tableContainerRef = useRef(null);

    const dispatch = useDispatch();

    const location = useLocation();
    const history = useHistory();

    const [tableData, setTableData] = useState([]);
    const [headersData, setHeadersData] = useState({});
    const [headersType, setHeadersType] = useState("Default Headers");
    const [tnaTableData, setTnaTableData] = useState({
        currentPage: 1,
        maxPage: 1,
        pageSize: 10,
        pageNumber: 1,
    })
    const [searchQuery, setSearchQuery] = useState('');
    
    const [sortConfig, setSortConfig] = useState({ sortedBy: "", sortedIn: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [refreshTable, setRefreshTable] = useState(false);
    const [enableJumpPage, setEnableJumpPage] = useState(false);
    const [filters, setFilters] = useState({
        onTrack: false,
        delayed: false
    });
    const [columnFilters, setColumnFilters] = useState({});
    const didMountRef = useRef(false);
    const [groupList, setGroupList] = useState([]);

    useEffect(() => {
        getTableData();
        handleApiChange(dispatch, "getHeadersRequest", {
            enterpriseName: "TURNINGCLOUD",
            displayName: "TNA_DASHBOARD_DATA",
            basedOn: "mainInner",
        })
        handleApiChange(dispatch, "getDropdownRequest", { type: "TNA_GROUPS" }, getPermissionKey("purchase", "tnaconfig", "view"));
    }, [])

     const handleJumpPage = () => {
        setEnableJumpPage(true);
    }

    useEffect(() => {
        if (getDropdownData?.isSuccess) {
            let data = getDropdownData?.data?.resource || []
            if (!data?.includes?.("Others")) {
                data?.push?.("Others");
            }
            setGroupList(data);
        }
    }, [getDropdownData?.isSuccess])

    const closeJumpPage = (e) => {
        if (jumpInputRef?.current && !jumpInputRef.current.contains(e.target)) {
            setEnableJumpPage(false);
        }
    }

    useEffect(() => {
            if (enableJumpPage) {
                setTimeout(() => document.addEventListener("click", closeJumpPage), 0)
            }
    
            return () => {
                setTimeout(() => document.removeEventListener("click", closeJumpPage), 0)
            };
        }, [enableJumpPage]);

    useEffect(() => {
        if(refreshTable) {
            getTableData();
            setRefreshTable(false);
        }
    }, [refreshTable])

    // Refetch from page 1 whenever the per-column filters change. Skip the
    // initial mount so we don't fire a duplicate request alongside getTableData().
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        getTableData({ pageNo: 1 });
    }, [columnFilters])

    useEffect(() => {
        if (generalStore?.getHeadersData?.isSuccess && generalStore?.getHeadersData?.data?.basedOn == "mainInner") {
            let data = generalStore?.getHeadersData?.data?.resource || {};
            let type = Object.keys(data?.["Custom Headers"] || {})?.length > 0 ? "Custom Headers" : "Default Headers"
            setHeadersData(data)
            setHeadersType(type)
        }

        if (getTableComponentListData?.isSuccess) {
            setTableData(getTableComponentListData?.data?.resource || []);
            setTnaTableData((prevState) => ({
                ...prevState,
                currentPage: getTableComponentListData?.data?.currPage,
                maxPage: getTableComponentListData?.data?.maxPage,
                pageSize: getTableComponentListData?.data?.pageSize,
                totalRecord: getTableComponentListData?.data?.totalRecord
            }));
        }
        else if (getTableComponentListData?.isError) {
            setTableData([]);
            setTnaTableData((prevState) => ({
                ...prevState,
                currentPage: 1,
                maxPage: 1,
                pageSize: 10,
                totalRecord: 0
            }));
        }
    }, [generalStore?.getHeadersData?.isSuccess, getTableComponentListData?.isSuccess, getTableComponentListData?.isError])

     const handleFilterChange = (filterType) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: !prev[filterType]
        }));
        setRefreshTable(true);
    };

    // Curried to match TableMainLevelFilters' applyFilter(filter)() contract.
    // Setting columnFilters triggers the refetch effect above.
    const applyColumnFilter = (filter) => () => {
        setColumnFilters(filter || {});
    };

    const clearColumnFilters = () => {
        setColumnFilters({});
    };

    const handleSort = (key) => {
        if (headersData?.columnType?.[key] === "NOSORT") return;
        setSortConfig(prev => ({
            sortedBy: key,
            sortedIn: prev.sortedBy === key && prev.sortedIn === "ASC" ? "DESC" : "ASC"
        }));
        setRefreshTable(true);
    };

    const getTableData = (data) => {
        let payload = {
            displayName: "TNA_DASHBOARD_DATA",
            filter: {
                ...cellFilter,
                ...columnFilters,
                ontrack_status: (filters.onTrack && filters.delayed) ? undefined : filters.onTrack ? "on-track" : filters?.delayed ? "delayed" : undefined
            },
            apiUrl: "/purchase/dashboard/tna/analysis",
            baseUrl: CONFIG.SM_BACKEND_BASE_URL,
            analysisType: "tabular",
            statusName: cellStatusName || "",
            pageNo: data?.pageNo ?? 1,
            pageSize: 15,
            search: searchQuery ?? "",
            sortedBy: sortConfig.sortedBy || "",
            sortedIn: sortConfig.sortedIn || ""
        };
        dispatchHandler(dispatch, "getTableComponentListRequest", payload, getPermissionKey("dashboard", "tna", "view"))
    }

    const rowVirtualizer = useVirtualizer({
        count: tableData?.length || 0,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 32, // Estimated row height in pixels
        overscan: 20, // Render 20 extra rows above/below viewport for smooth scrolling
    });

    const changePage = (page) => {
        setTnaTableData((prevState) => ({
            ...prevState,
            current: page,
        }));
        getTableData({
            pageNo: page,
        });
    }

    const renderCellContent = (key, row) => {
        if (key === "status_name" || key == "next_status") {
            const rawVal = row?.[key] || "N/A";
            return (
                <TNATimelinePopover
                    designNo={row?.design_no || row?.designNo || row?.tna_check || ""}
                    orderNumber={row?.gsl_po || row?.orderNumber || row?.orderNo || row?.poNo || ""}
                    groupName={row?.group_name || row?.groupName || ""} groupList={groupList} setGroupList={setGroupList}
                >
                    <div className="flex items-center gap-1.5 cursor-pointer group px-[10px] py-[4px] bg-white border border-[#DAE4EE] hover:border-[#5d5fef] rounded-[4px] shadow-2xs transition-all text-[#111D31] text-[11px] font-medium max-w-[170px] justify-between">
                        <span className="truncate">{rawVal}</span>
                        <ChevronDown size={13} className="text-[#707494] group-hover:text-[#5d5fef] shrink-0" />
                    </div>
                </TNATimelinePopover>
            );
        }

        if (key === "ontrack_status") {
            return (
                <div className="text-[11px]">
                    <div className={`font-medium whitespace-nowrap ${row.ontrack_status?.toLowerCase?.() === 'on-track'
                            ? 'text-teal-600'
                            : 'text-red-500'
                        }`}>
                        {row.ontrack_status || "N/A"}
                    </div>
                </div>
            );
        }

        if (key === "gsl_po") {
            return (
                <div className="flex items-center gap-2 whitespace-nowrap">
                    <span className="rightArrowLabel px-1.5 py-[1px] !text-xs !font-semibold !text-[#111D31] border border-transparent cursor-pointer hover:border-[#5d5fef] rounded-[3px]" onClick={() => handleChange("viewDocument", row)}>
                        {row?.gsl_po ?? "N/A"}
                    </span>
                </div>
            );
        }

        return <TextWithTooltip text={row?.[key] ?? "N/A"} width="100%" className='whitespace-nowrap !max-w-[180px]' />;
    };

    const renderTable = () => {
        if (getTableComponentListData?.isLoading) {
            return <TableLoader className="!px-0 mt-[10px] flex-1 overflow-auto" />
        }

        const virtualItems = rowVirtualizer.getVirtualItems();
        // +1 for the leading spacer column that aligns the filter row's
        // fix-action cell (Clear All) with the data columns.
        const totalColumns = (Object.keys(headersData[headersType] ?? {}).length || 10) + 1;
        const totalHeight = rowVirtualizer.getTotalSize();
        const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
        const paddingBottom = virtualItems.length > 0
            ? totalHeight - (virtualItems[virtualItems.length - 1].end)
            : 0;

        return (
            <div className="flex-1 min-h-0 mx-3 mb-3 mt-2 flex flex-col">
                <div
                    ref={tableContainerRef}
                    className="table-v2 bg-white relative flex-1 min-h-0 tna-dashboard-table"
                >
                    <table className="min-w-full" style={{ tableLayout: "auto", borderCollapse: "separate", borderSpacing: 0 }}>
                        <thead className="bg-[#F8F8F8] border-b border-gray-200 sticky top-0 !z-[1]">
                            <tr>
                                {/* Leading spacer aligning with the filter row's fix-action cell. */}
                                <th className="!border-r-0" />
                                {Object.keys(headersData[headersType] ?? {})?.length > 0 &&
                                    Object.keys(headersData[headersType])?.map((key) => {
                                        const sortable = headersData?.columnType?.[key] !== "NOSORT";
                                        const isActive = sortConfig.sortedBy === key && !!sortConfig.sortedIn;
                                        return (
                                            <th
                                                key={key}
                                                className={`!border-r-0 whitespace-nowrap group !py-[6px] !px-[2px] ${sortable ? "cursor-pointer select-none" : ""}`}
                                                onClick={sortable ? () => handleSort(key) : undefined}
                                            >
                                                <span className="inline-flex items-center gap-1 rounded-[5px] w-full group-hover:bg-[#fff] px-[8px] py-[2px] border-transparent border group-hover:border-[#dae4ee]">
                                                    {headersData[headersType]?.[key]}
                                                    {sortable && (
                                                        isActive
                                                            ? (sortConfig.sortedIn === "ASC"
                                                                ? <ArrowUp size={13} strokeWidth={2.2} className="shrink-0 text-[#5d5fef] invisible group-hover:visible" />
                                                                : <ArrowDown size={13} strokeWidth={2.2} className="shrink-0 text-[#5d5fef] invisible group-hover:visible" />)
                                                            : <ArrowUp size={13} strokeWidth={1.6} className="shrink-0 text-[#94A3B8] invisible group-hover:visible" />
                                                    )}
                                                </span>
                                            </th>
                                        );
                                    })
                                }
                            </tr>
                            {Object.keys(headersData?.[headersType] ?? {}).length > 0 && (
                                <TableMainLevelFilters
                                    filterItems={headersData}
                                    appliedFilters={columnFilters}
                                    applyFilter={applyColumnFilter}
                                    clearMainFilter={clearColumnFilters}
                                    headersData={{ main: { data: headersData } }}
                                    tableConfig={{}}
                                    tableDataPayload={{ filter: columnFilters }}
                                    generalStore={generalStore}
                                    quickFilterItems={[]}
                                    stickyCols={[]}
                                    isSaveFilterRequire={false}
                                    hideSaveFilter={true}
                                    getTableDataSuccess={getTableComponentListData?.isSuccess}
                                    isLoading={false}
                                    saveSuccess={false}
                                    closeFilterModal={() => {}}
                                    saveFilters={() => {}}
                                    saveQuickFilter={() => {}}
                                    location={location}
                                    history={history}
                                    dispatch={dispatch}
                                    handleApiChange={(action, payload, extra) => handleApiChange(dispatch, action, payload, extra)}
                                />
                            )}
                        </thead>
                        <tbody className="bg-white divide-y divide-borderSlateGrey">
                            {tableData?.length > 0 ? (
                                <>
                                    {paddingTop > 0 && (
                                        <tr key="virtual-spacer-top">
                                            <td style={{ height: `${paddingTop}px` }} colSpan={totalColumns} />
                                        </tr>
                                    )}
                                    {virtualItems.map((virtualRow) => {
                                        const row = tableData?.[virtualRow.index];
                                        // Tint the whole row by delivery status: on-track → green,
                                        // anything else with a status (delayed) → red. No status → neutral.
                                        const status = row?.ontrack_status?.toLowerCase?.();
                                        const rowTint = status === 'on-track'
                                            ? 'bg-emerald-50 hover:bg-emerald-100'
                                            : status
                                                ? 'bg-red-50 hover:bg-red-100'
                                                : 'hover:bg-gray-50';
                                        return (
                                            <tr
                                                key={virtualRow.index}
                                                data-index={virtualRow.index}
                                                ref={rowVirtualizer.measureElement}
                                                className={`cursor-pointer h-[32px] ${rowTint}`}
                                            >
                                                {/* Leading spacer aligning with the header/filter fix-action column. */}
                                                <td className="!border-r-0" />
                                                {Object.keys(headersData[headersType] ?? {})?.map((key) => (
                                                    <td key={key} className="!border-r-0 whitespace-nowrap">
                                                        {renderCellContent(key, row)}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                    {paddingBottom > 0 && (
                                        <tr key="virtual-spacer-bottom">
                                            <td style={{ height: `${paddingBottom}px` }} colSpan={totalColumns} />
                                        </tr>
                                    )}
                                </>
                            ) : (
                                <tr>
                                    <td
                                        className="p-[8px] whitespace-nowrap text-[11px] text-gray-900 h-full"
                                        colSpan={totalColumns}
                                    >
                                        <div className='h-full flex items-center justify-center py-6'>
                                            No Data Found!
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    const setPageNumberValue = (e) => {
        let pageNumber = Number(e.target.value),
            valid = e.target.value == "" || (/^\d*$/.test(pageNumber) && pageNumber > 0 && pageNumber <= tnaTableData?.maxPage);
        if (valid) {
            setTnaTableData((prevState) => ({
                ...prevState,
                pageNumber: pageNumber,
            }));
            if (e.key == "Enter" && pageNumber && pageNumber != tnaTableData?.currentPage) {
                changePage(pageNumber);
                setEnableJumpPage(false);
            }
        }
    }

    const handleChange = (type, data) => {
        switch (type) {
            case "search": {
                setCurrentPage(1);
                break;
            }
            case "searchTableData": {
                setSearchQuery(data?.target?.value ?? "");
                if(!(data?.target?.value ?? "")) {
                    setRefreshTable(true)
                }
                break;
            }
            case "viewDocument": {
                let obj = modifyDataForDocumentViewerUrlInsertion("encode", {
                    documentId: data?.gsl_po,
                    documentNo: data?.gsl_po,
                    documentType: "PO",
                    poType: data?.poType,
                    ontrack_status: data?.ontrack_status
                });

                // Update the url search params here.
                setPayloadAsURLParams({
                    data: {
                        ...getPayloadFromURLParams({
                            l: location,
                        }),
                        documentViewerConfig: obj,
                    },
                    l: location,
                    h: history,
                });
                break;
            }
        }
    }

    const totalRecord = tnaTableData?.totalRecord ?? 0;

    return (
        <div className='flex flex-col h-full overflow-hidden bg-white'>
            <GenericFormHeaderComponent
                closeModal={props.closeModalV2}
                label={{
                    contentPrimary: cellStatusName ? `PO — ${cellStatusName}` : 'PO Details',
                    contentSecondry: `${totalRecord.toLocaleString('en-IN')} record${totalRecord === 1 ? '' : 's'}`,
                }}
            />
            <div className="bg-white px-3 border-b border-[#EEF0F6] shrink-0">
                <div className="flex items-center justify-between new-gen-pagination !m-0">
                    <div className="flex items-center gap-4">
                    </div>

                    <div className="flex items-center gap-3">
                        <GenericInput
                            type="text"
                            placeholder="Search"
                            value={searchQuery}
                            dependentFieldsObject={[searchQuery]}
                            onChange={(e) => handleChange("searchTableData", e)}
                            onKeyDown={(e) => e.key == 'Enter' ? setRefreshTable(true) : false}
                            iconLeft={<Search className="w-[16px] h-[16px] text-gray-400 mr-[5px]" />}
                            genInputExtraClass="!m-0 !w-[150px]"
                        />

                        <span className='flex w-[1px] h-[16px] bg-[#dae4ee]'></span>

                        <div className="ngp-right tablePagination">
                            <div className="nt-btn">
                                <div className="pagination-inner">
                                    <ul className="pagination-item">
                                        <li className='!flex !items-center'>
                                            <button className={`${tnaTableData?.currentPage == 1 && "gen-action-btn-disable disable-"}first-btn !h-[32px] !border !border-solid !border-[#dae4ee] !rounded-[3px]`} disabled={tnaTableData?.currentPage == 1} onClick={() => changePage(1)}>
                                                <span className="page-item-btn-inner">
                                                    <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M10.8977 6.49114C11.2557 6.84912 11.2557 7.42952 10.8977 7.7875L6.96254 11.7227L10.8977 15.6578C11.2557 16.0158 11.2557 16.5962 10.8977 16.9542C10.5397 17.3122 9.95931 17.3122 9.60133 16.9542L5.018 12.3708C4.66002 12.0129 4.66002 11.4325 5.018 11.0745L9.60133 6.49114C9.95931 6.13316 10.5397 6.13316 10.8977 6.49114Z" fill="#23235F" />
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M17.3143 6.49114C17.6723 6.84912 17.6723 7.42952 17.3143 7.7875L13.3792 11.7227L17.3143 15.6578C17.6723 16.0158 17.6723 16.5962 17.3143 16.9542C16.9563 17.3122 16.3759 17.3122 16.018 16.9542L11.4346 12.3708C11.0766 12.0129 11.0766 11.4325 11.4346 11.0745L16.018 6.49114C16.3759 6.13316 16.9563 6.13316 17.3143 6.49114Z" fill="#23235F" />
                                                    </svg>
                                                </span>
                                            </button>
                                        </li>
                                        <li className='!flex !items-center'>
                                            <button className={`${tnaTableData?.currentPage == 1 && "gen-action-btn-disable dis-"}prev-btn !h-[32px] !border !border-solid !border-[#dae4ee] !rounded-[3px] !justify-center`} disabled={tnaTableData?.currentPage == 1} onClick={() => changePage(tnaTableData?.currentPage - 1)}>
                                                <span className="page-item-btn-inner">
                                                    <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M6.89781 0.491142C7.2558 0.849122 7.2558 1.42952 6.89781 1.7875L2.96266 5.72266L6.89781 9.65781C7.2558 10.0158 7.2558 10.5962 6.89781 10.9542C6.53983 11.3122 5.95943 11.3122 5.60145 10.9542L1.01812 6.37084C0.660139 6.01286 0.660139 5.43246 1.01812 5.07448L5.60145 0.491142C5.95943 0.133161 6.53983 0.133161 6.89781 0.491142Z" fill="#23235F" />
                                                    </svg>
                                                </span>
                                            </button>
                                        </li>
                                        <li className='!flex !items-center'>
                                            {!enableJumpPage ? <button className="pi-number-btn !h-[32px] !border !border-solid !border-[#dae4ee] !rounded-[3px]" onClick={() => handleJumpPage()}>
                                                <span>{tnaTableData?.currentPage}<label className='m-0'>of</label>{tnaTableData?.maxPage}</span>
                                            </button> : <input
                                                ref={jumpInputRef}
                                                className="inline-block w-[60px] h-[28px] border border-[#dae4ee] px-[3px] pl-[5px] rounded-[4px] shadow-[0px_0px_0px_2px_#DADAFF] font-medium text-xs"
                                                onChange={setPageNumberValue}
                                                onKeyDown={setPageNumberValue}
                                                type="number"
                                                autoComplete="off"
                                                value={tnaTableData?.pageNumber}
                                            />}
                                        </li>

                                        <li className='!flex !items-center'>
                                            <button className={`${tnaTableData?.currentPage == tnaTableData?.maxPage && "gen-action-btn-disable dis-"}next-btn !h-[32px] !border !border-solid !border-[#dae4ee] !rounded-[3px] !justify-center`} disabled={tnaTableData?.currentPage == tnaTableData?.maxPage} onClick={() => changePage(tnaTableData?.currentPage + 1)}>
                                                <span className="page-item-btn-inner">
                                                    <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M0.934461 0.491142C0.57648 0.849122 0.57648 1.42952 0.934461 1.7875L4.86961 5.72266L0.934461 9.65781C0.57648 10.0158 0.57648 10.5962 0.934461 10.9542C1.29244 11.3122 1.87284 11.3122 2.23082 10.9542L6.81416 6.37084C7.17214 6.01286 7.17214 5.43246 6.81416 5.07448L2.23082 0.491142C1.87284 0.133161 1.29244 0.133161 0.934461 0.491142Z" fill="#23235F" />
                                                    </svg>
                                                </span>
                                            </button>
                                        </li>
                                        <li className='!flex !items-center'>
                                            <button className={`${tnaTableData?.currentPage == tnaTableData?.maxPage && "gen-action-btn-disable dis-"}last-btn !h-[32px] !border !border-solid !border-[#dae4ee] !rounded-[3px]`} disabled={tnaTableData?.currentPage == tnaTableData?.maxPage} onClick={() => changePage(tnaTableData?.maxPage)}>
                                                <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M11.9346 6.49114C11.5766 6.84912 11.5766 7.42952 11.9346 7.7875L15.8697 11.7227L11.9346 15.6578C11.5766 16.0158 11.5766 16.5962 11.9346 16.9542C12.2926 17.3122 12.873 17.3122 13.2309 16.9542L17.8143 12.3708C18.1723 12.0129 18.1723 11.4325 17.8143 11.0745L13.2309 6.49114C12.873 6.13316 12.2926 6.13316 11.9346 6.49114Z" fill="#23235F" />
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M5.51796 6.49114C5.15998 6.84912 5.15998 7.42952 5.51796 7.7875L9.45311 11.7227L5.51796 15.6578C5.15998 16.0158 5.15998 16.5962 5.51796 16.9542C5.87594 17.3122 6.45634 17.3122 6.81432 16.9542L11.3977 12.3708C11.7556 12.0129 11.7556 11.4325 11.3977 11.0745L6.81432 6.49114C6.45634 6.13316 5.87594 6.13316 5.51796 6.49114Z" fill="#23235F" />
                                                </svg>
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <span className='flex w-[1px] h-[16px] bg-[#dae4ee]'></span>

                        <div className="flex items-center gap-4 border border-solid border-[#dae4ee] rounded-[3px] h-[32px] px-[8px]">
                            <div className="flex items-center gap-2">
                                <label className={`generic-checkbox !block !font-normal`}>
                                    <input
                                        type="checkbox"
                                        checked={filters.onTrack}
                                        onChange={() => handleFilterChange('onTrack')}
                                    />
                                    <span className="checkmark1 !top-0"></span>
                                    <span className="text-xs font-semibold text-[#0d9488]">On Track</span>
                                    {/* <span className="text-xs font-semibold text-fontBlack ml-[10px]">{statusName?.on_track_count ?? 0}</span> */}
                                </label>
                            </div>
                            <div className="flex items-center gap-2">
                                <label className={`generic-checkbox !block !font-normal`}>
                                    <input
                                        type="checkbox"
                                        checked={filters.delayed}
                                        onChange={() => handleFilterChange('delayed')}
                                    />
                                    <span className="checkmark1 !top-0"></span>
                                    <span className="text-xs font-semibold text-[#FD3757]">Delayed</span>
                                    {/* <span className="text-xs font-semibold text-fontBlack ml-[10px]">{statusName?.delayed_count ?? 0}</span> */}
                                </label>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {renderTable()}
        </div>
    )
}

export default ViewCountData